import { StyledCardStack, StyledHeadingText, StyledList } from '@common/styled';
import { EntityEvents } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { EventName, EventType, metrics } from '@prophecy/metrics';
import { theme } from '@prophecy/theme';
import { Centered, Stack, Text, Button, Card } from '@prophecy/ui-v3';
import { message } from '@prophecy/ui-v3/ui-compat';
import DocumentTitle from '@prophecy/utils/react/DocumentTitle';
import { useMutation as useReactMutation } from '@tanstack/react-query';

import { useAppMetadata } from '../../../context/appMetadata';
import { useMixPanel } from '../../../context/mixpanel/context';
import { useUser } from '../../../context/user';
import { upgradePlan } from '../../../data/apis/api';

type UpgradeResp = {
  success: boolean;
  message: string;
};

export default function UpgradePlan() {
  const userInfo = useAppMetadata()?.user;
  const user = useUser().user;
  const isAdmin = userInfo?.type === UserTypes.ClusterAdmin;
  const billingDetails = userInfo?.trialInfo?.billingDetails;
  const { track } = useMixPanel();
  const upgradeMutation = useReactMutation<UpgradeResp, UpgradeResp, unknown>(['GenerateToken'], () => {
    track(EntityEvents.settings.home.upgradePlanStart, { isAdmin });
    metrics.track({
      eventName: EventName.ui.upgrade.plan.user.start,
      eventType: EventType.userAction,
      payload: { isAdmin }
    });
    return upgradePlan();
  });
  const handleUpgrade = async () => {
    await upgradeMutation.mutateAsync(
      {},
      {
        onSuccess: (resp) => {
          if (resp.success) {
            window.location.reload();
          } else {
            message.error({ content: resp?.message || 'Update plan failed' });
          }
        },
        onError: (error) => {
          message.error({ content: error?.message || 'Update plan failed' });
        }
      }
    );
  };
  return (
    <Stack width='100%' height='100%' alignY='center' align='center'>
      <DocumentTitle title='Upgrade Plan' />
      <Centered>
        <Card>
          <StyledCardStack gap={theme.spaces.x24} width='100%' align='center' padding={theme.spaces.x8}>
            <StyledHeadingText level='sm' weight={theme.fontWeight.medium}>
              Your Trial has been ended
            </StyledHeadingText>
            <Text level='md'>Hi {user?.firstName} 👋</Text>
            <Text level='md' elementType='p'>
              Your 21-day trial of Prophecy has expired. Your account no longer has access to:
            </Text>
            <StyledList>
              <li>Projects</li>
              <li>Datasets</li>
              <li>Pipelines</li>
              <li>Lineage</li>
            </StyledList>
            <div>
              <Text level='md'>Not quite ready to say goodbye? Subscribe now </Text>
              <Text level='md'>to pick up where you left off, or contact</Text>
              <a href='mailto:admin@prophecy.io' data-test-id='upgrade-plan-admin-prophecy-io-email-link'>
                admin@prophecy.io
              </a>{' '}
              to request a trial extension.
            </div>
            {isAdmin && (
              <Button
                onClick={handleUpgrade}
                loading={upgradeMutation.isLoading}
                data-test-id='upgrade-plan-add-payment-details-button'>
                Add Payment Details Now
                {billingDetails?.price && (
                  <span>
                    ({billingDetails?.price} {billingDetails?.dimension})
                  </span>
                )}
              </Button>
            )}
          </StyledCardStack>
        </Card>
      </Centered>
    </Stack>
  );
}
